import {
    createTableCell,
    createTableRow,
    getHeaderRowCount,
    getHeaderRows,
    withHeaderRows,
} from '@/lib/table-utils';
import { TableBlock, TableCell, TableCellColor, TableRow } from '@/types/report-payload';
import {
    CellPosition,
    SelectionRange,
    TableSection,
    getSelectionRect,
    getSelectionRectGlobal,
    globalRowToPosition,
} from './TableSelection';

type CellPositionMap = Map<string, CellPosition>;

const cloneRow = (row: TableRow): TableRow => ({
    ...row,
    cells: row.cells.map((cell) => ({ ...cell })),
});

const cloneBlock = (block: TableBlock): TableBlock => ({
    ...block,
    // As faixas também são clonadas: os reducers abaixo mutam células no lugar,
    // e sem isto a edição vazaria para o bloco de origem.
    ...(block.header_bands ? { header_bands: block.header_bands.map(cloneRow) } : {}),
    header: cloneRow(block.header),
    rows: block.rows.map(cloneRow),
    columnWidths: [...block.columnWidths],
});

/** Aplica `mutate` a cada linha do cabeçalho, preservando o invariante. */
const mapHeaderRows = (block: TableBlock, mutate: (row: TableRow) => void): TableBlock => {
    const rows = getHeaderRows(block);
    rows.forEach(mutate);
    return withHeaderRows(block, rows);
};

const getCellAt = (
    block: TableBlock,
    section: TableSection,
    rowIndex: number,
    colIndex: number,
): TableCell | null => {
    if (section === 'header') {
        return getHeaderRows(block)[rowIndex]?.cells[colIndex] || null;
    }

    const row = block.rows[rowIndex];
    if (!row) return null;
    return row.cells[colIndex] || null;
};

const setCellAt = (
    block: TableBlock,
    section: TableSection,
    rowIndex: number,
    colIndex: number,
    updater: (cell: TableCell) => void,
) => {
    const cell = getCellAt(block, section, rowIndex, colIndex);
    if (!cell) return;
    updater(cell);
};

const collectOriginIds = (
    block: TableBlock,
    selection: SelectionRange,
    cellIdToPosition: CellPositionMap,
) => {
    const rect = getSelectionRect(selection, getHeaderRowCount(block));
    const originIds = new Set<string>();
    for (let row = rect.rowStart; row <= rect.rowEnd; row += 1) {
        for (let col = rect.colStart; col <= rect.colEnd; col += 1) {
            const cell = getCellAt(block, selection.start.section, row, col);
            if (!cell) continue;
            const originId = cell.mergedInto || cell.id;
            const originPos = cellIdToPosition.get(originId);
            if (!originPos || originPos.section !== selection.start.section) continue;
            originIds.add(originId);
        }
    }
    return originIds;
};

const unmergeOrigins = (
    block: TableBlock,
    originIds: Set<string>,
    cellIdToPosition: CellPositionMap,
    section: TableSection,
) => {
    originIds.forEach((originId) => {
        const originPos = cellIdToPosition.get(originId);
        if (!originPos || originPos.section !== section) return;

        const originCell = getCellAt(block, section, originPos.rowIndex, originPos.colIndex);
        if (!originCell) return;

        const rowSpan = originCell.rowSpan || 1;
        const colSpan = originCell.colSpan || 1;

        originCell.rowSpan = undefined;
        originCell.colSpan = undefined;
        originCell.mergedInto = undefined;

        for (let r = originPos.rowIndex; r < originPos.rowIndex + rowSpan; r += 1) {
            for (let c = originPos.colIndex; c < originPos.colIndex + colSpan; c += 1) {
                if (r === originPos.rowIndex && c === originPos.colIndex) continue;
                setCellAt(block, section, r, c, (cell) => {
                    if (cell.mergedInto === originId) {
                        cell.mergedInto = undefined;
                    }
                });
            }
        }
    });
};

export const unmergeCells = (
    block: TableBlock,
    selection: SelectionRange,
    cellIdToPosition: CellPositionMap,
): TableBlock => {
    const next = cloneBlock(block);
    const originIds = collectOriginIds(next, selection, cellIdToPosition);
    unmergeOrigins(next, originIds, cellIdToPosition, selection.start.section);
    return next;
};

export const mergeCells = (
    block: TableBlock,
    selection: SelectionRange,
    cellIdToPosition: CellPositionMap,
): TableBlock => {
    const rect = getSelectionRect(selection, getHeaderRowCount(block));
    const rowSpan = rect.rowEnd - rect.rowStart + 1;
    const colSpan = rect.colEnd - rect.colStart + 1;

    if (rowSpan === 1 && colSpan === 1) return block;

    const unmerged = unmergeCells(block, selection, cellIdToPosition);
    const next = cloneBlock(unmerged);
    const section = selection.start.section;

    const originCell = getCellAt(next, section, rect.rowStart, rect.colStart);
    if (!originCell) return block;

    originCell.rowSpan = rowSpan > 1 ? rowSpan : undefined;
    originCell.colSpan = colSpan > 1 ? colSpan : undefined;
    originCell.mergedInto = undefined;

    for (let r = rect.rowStart; r <= rect.rowEnd; r += 1) {
        for (let c = rect.colStart; c <= rect.colEnd; c += 1) {
            if (r === rect.rowStart && c === rect.colStart) continue;
            setCellAt(next, section, r, c, (cell) => {
                cell.mergedInto = originCell.id;
                cell.value = '';
                cell.rowSpan = undefined;
                cell.colSpan = undefined;
            });
        }
    }

    return next;
};

/**
 * Paints the text of every cell in the selection, or clears it when `color` is
 * undefined. Unlike merge and unmerge this walks the selection in global-row
 * space, so a selection that spans header + body colors both.
 */
export const setCellsColor = (
    block: TableBlock,
    selection: SelectionRange,
    cellIdToPosition: CellPositionMap,
    color?: TableCellColor,
): TableBlock => {
    const next = cloneBlock(block);
    const headerRowCount = getHeaderRowCount(block);
    const rect = getSelectionRectGlobal(selection, headerRowCount);
    const targetIds = new Set<string>();

    for (let globalRow = rect.rowStart; globalRow <= rect.rowEnd; globalRow += 1) {
        const { section, rowIndex } = globalRowToPosition(globalRow, headerRowCount);
        for (let col = rect.colStart; col <= rect.colEnd; col += 1) {
            const cell = getCellAt(next, section, rowIndex, col);
            if (!cell) continue;
            // A covered cell shows nothing of its own: color the origin it was
            // merged into, which is the cell actually holding the text.
            targetIds.add(cell.mergedInto || cell.id);
        }
    }

    targetIds.forEach((cellId) => {
        const position = cellIdToPosition.get(cellId);
        if (!position) return;
        setCellAt(next, position.section, position.rowIndex, position.colIndex, (cell) => {
            cell.color = color;
        });
    });

    return next;
};

const collectIntersectingOrigins = (
    block: TableBlock,
    section: TableSection,
    rowIndex: number | null,
    colIndex: number | null,
    cellIdToPosition: CellPositionMap,
) => {
    const originIds = new Set<string>();
    const rows = section === 'header' ? getHeaderRows(block) : block.rows;

    rows.forEach((row) => {
        row.cells.forEach((cell) => {
            const isOrigin = (cell.rowSpan || 1) > 1 || (cell.colSpan || 1) > 1;
            if (!isOrigin || cell.mergedInto) return;

            const originPos = cellIdToPosition.get(cell.id);
            if (!originPos || originPos.section !== section) return;

            const rowSpan = cell.rowSpan || 1;
            const colSpan = cell.colSpan || 1;
            const rowEnd = originPos.rowIndex + rowSpan - 1;
            const colEnd = originPos.colIndex + colSpan - 1;

            if (rowIndex !== null && rowIndex >= originPos.rowIndex && rowIndex <= rowEnd) {
                originIds.add(cell.id);
            }

            if (colIndex !== null && colIndex >= originPos.colIndex && colIndex <= colEnd) {
                originIds.add(cell.id);
            }
        });
    });

    return originIds;
};

/**
 * Uma linha de cabeçalho só pode nascer ACIMA da folha.
 *
 * `withHeaderRows` promove sempre a última linha a `header`; deixar inserir
 * abaixo dela faria de uma linha em branco a folha, e com ela iriam os rótulos
 * de coluna dos gráficos, a coluna Classificação e a contagem de
 * `columnWidths` — tudo em silêncio.
 */
export const canInsertHeaderRowAt = (block: TableBlock, rowIndex: number) =>
    rowIndex <= getHeaderRowCount(block) - 1;

/**
 * A folha nunca é excluída. Excluí-la promoveria uma faixa ao seu lugar, e com
 * isso mudariam os IDS das células do cabeçalho — que é o que
 * `chart_source.x_axis_column_id` guarda. Todo gráfico ligado à tabela ficaria
 * pendurado, sem erro nenhum na tela.
 */
export const canDeleteHeaderRowAt = (block: TableBlock, rowIndex: number) =>
    getHeaderRowCount(block) > 1 && rowIndex < getHeaderRowCount(block) - 1;

export const insertRow = (
    block: TableBlock,
    section: TableSection,
    rowIndex: number,
    cellIdToPosition: CellPositionMap,
): TableBlock => {
    const next = cloneBlock(block);
    const originIds = collectIntersectingOrigins(next, section, rowIndex, null, cellIdToPosition);
    unmergeOrigins(next, originIds, cellIdToPosition, section);

    const newRow = createTableRow(next.columnWidths.length);

    if (section === 'header') {
        // Clampado aqui, e não só na UI, porque o atalho de teclado não passa
        // pelo gate visual.
        const headerRows = getHeaderRows(next);
        const at = Math.min(Math.max(rowIndex, 0), headerRows.length - 1);
        headerRows.splice(at, 0, newRow);
        return withHeaderRows(next, headerRows);
    }

    next.rows.splice(rowIndex, 0, newRow);
    return next;
};

export const deleteRow = (
    block: TableBlock,
    section: TableSection,
    rowIndex: number,
    cellIdToPosition: CellPositionMap,
): TableBlock => {
    if (section === 'header' && !canDeleteHeaderRowAt(block, rowIndex)) return block;

    const next = cloneBlock(block);
    const originIds = collectIntersectingOrigins(next, section, rowIndex, null, cellIdToPosition);
    unmergeOrigins(next, originIds, cellIdToPosition, section);

    if (section === 'header') {
        const headerRows = getHeaderRows(next);
        headerRows.splice(rowIndex, 1);
        return withHeaderRows(next, headerRows);
    }

    next.rows.splice(rowIndex, 1);
    return next;
};

export const insertColumn = (
    block: TableBlock,
    colIndex: number,
    cellIdToPosition: CellPositionMap,
): TableBlock => {
    const next = cloneBlock(block);
    const headerOriginIds = collectIntersectingOrigins(
        next,
        'header',
        null,
        colIndex,
        cellIdToPosition,
    );
    const bodyOriginIds = collectIntersectingOrigins(
        next,
        'body',
        null,
        colIndex,
        cellIdToPosition,
    );
    unmergeOrigins(next, headerOriginIds, cellIdToPosition, 'header');
    unmergeOrigins(next, bodyOriginIds, cellIdToPosition, 'body');

    const withColumn = mapHeaderRows(next, (row) => {
        row.cells.splice(colIndex, 0, createTableCell(''));
    });

    withColumn.rows.forEach((row) => {
        row.cells.splice(colIndex, 0, createTableCell(''));
    });

    withColumn.columnWidths.splice(colIndex, 0, 150);
    return withColumn;
};

export const deleteColumn = (
    block: TableBlock,
    colIndex: number,
    cellIdToPosition: CellPositionMap,
): TableBlock => {
    const next = cloneBlock(block);
    const headerOriginIds = collectIntersectingOrigins(
        next,
        'header',
        null,
        colIndex,
        cellIdToPosition,
    );
    const bodyOriginIds = collectIntersectingOrigins(
        next,
        'body',
        null,
        colIndex,
        cellIdToPosition,
    );
    unmergeOrigins(next, headerOriginIds, cellIdToPosition, 'header');
    unmergeOrigins(next, bodyOriginIds, cellIdToPosition, 'body');

    const withoutColumn = mapHeaderRows(next, (row) => {
        row.cells.splice(colIndex, 1);
    });

    withoutColumn.rows.forEach((row) => row.cells.splice(colIndex, 1));
    withoutColumn.columnWidths.splice(colIndex, 1);
    return withoutColumn;
};

const hasMergedColumn = (block: TableBlock, colIndex: number) => {
    const rows = [...getHeaderRows(block), ...block.rows];

    for (let r = 0; r < rows.length; r += 1) {
        const row = rows[r];
        for (let c = 0; c < row.cells.length; c += 1) {
            const cell = row.cells[c];
            if (c === colIndex && cell.mergedInto) return true;

            const rowSpan = cell.rowSpan || 1;
            const colSpan = cell.colSpan || 1;

            if ((rowSpan > 1 || colSpan > 1) && c <= colIndex) {
                const colEnd = c + colSpan - 1;
                if (colIndex <= colEnd) return true;
            }
        }
    }

    return false;
};

const hasMergedRow = (block: TableBlock, rowIndex: number) => {
    const rows = [...getHeaderRows(block), ...block.rows];

    for (let r = 0; r < rows.length; r += 1) {
        const row = rows[r];
        for (let c = 0; c < row.cells.length; c += 1) {
            const cell = row.cells[c];
            if (r === rowIndex && cell.mergedInto) return true;

            const rowSpan = cell.rowSpan || 1;
            const colSpan = cell.colSpan || 1;

            if ((rowSpan > 1 || colSpan > 1) && r <= rowIndex) {
                const rowEnd = r + rowSpan - 1;
                if (rowIndex <= rowEnd) return true;
            }
        }
    }

    return false;
};

export const reorderColumn = (block: TableBlock, fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return { ok: true, block };
    if (hasMergedColumn(block, fromIndex) || hasMergedColumn(block, toIndex)) {
        return { ok: false, block };
    }

    const next = mapHeaderRows(cloneBlock(block), (row) => {
        const [movedCell] = row.cells.splice(fromIndex, 1);
        row.cells.splice(toIndex, 0, movedCell);
    });

    next.rows.forEach((row) => {
        const [movedCell] = row.cells.splice(fromIndex, 1);
        row.cells.splice(toIndex, 0, movedCell);
    });

    const [movedWidth] = next.columnWidths.splice(fromIndex, 1);
    next.columnWidths.splice(toIndex, 0, movedWidth);

    return { ok: true, block: next };
};

export const reorderRow = (block: TableBlock, fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return { ok: true, block };
    // Converte índice de corpo para a grade combinada. Fixo em 1 isto
    // consultaria a linha errada numa tabela com faixa: recusaria reordenações
    // legítimas e deixaria passar as que destroem uma mescla.
    const rowOffset = getHeaderRowCount(block);
    if (hasMergedRow(block, fromIndex + rowOffset) || hasMergedRow(block, toIndex + rowOffset)) {
        return { ok: false, block };
    }

    const next = cloneBlock(block);
    const [movedRow] = next.rows.splice(fromIndex, 1);
    next.rows.splice(toIndex, 0, movedRow);
    return { ok: true, block: next };
};
